import { MotivoCnpjInvalido } from './enumeradores';
import { ResultadoValidacaoCnpj } from './tipos';
import { calcularDigitoVerificador, PESOS_DV1, PESOS_DV2 } from './calcular-dv';
import { normalizarCnpj } from './normalizar-cnpj';

/**
 * Sequências numéricas repetidas rejeitadas mesmo quando o DV é matematicamente válido.
 * Conforme prática consolidada na validação de documentos fiscais.
 */
const SEQUENCIAS_BLOQUEADAS = new Set([
  '00000000000000',
  '11111111111111',
  '22222222222222',
  '33333333333333',
  '44444444444444',
  '55555555555555',
  '66666666666666',
  '77777777777777',
  '88888888888888',
  '99999999999999',
]);

/** Código ASCII de '0' (48). */
const COD_0 = 48;
/** Código ASCII de '9' (57). */
const COD_9 = 57;
/** Código ASCII de 'A' (65). */
const COD_A = 65;
/** Código ASCII de 'Z' (90). */
const COD_Z = 90;

/** Mensagens de erro indexadas por motivo de invalidez. */
const MENSAGENS: Record<MotivoCnpjInvalido, string> = {
  [MotivoCnpjInvalido.Vazio]:
    'O CNPJ informado está vazio.',
  [MotivoCnpjInvalido.Nulo]:
    'O CNPJ informado é nulo ou indefinido.',
  [MotivoCnpjInvalido.TamanhoInvalido]:
    'O CNPJ deve possuir exatamente 14 caracteres após a normalização.',
  [MotivoCnpjInvalido.CaracteresInvalidos]:
    'O CNPJ contém caracteres inválidos. São permitidos apenas [0-9A-Z] nas posições 1 a 12.',
  [MotivoCnpjInvalido.DigitosVerificadoresNaoNumericos]:
    'Os dígitos verificadores (posições 13 e 14) devem ser numéricos.',
  [MotivoCnpjInvalido.DigitoVerificadorInvalido]:
    'Os dígitos verificadores do CNPJ são inválidos.',
  [MotivoCnpjInvalido.SequenciaRepetida]:
    'O CNPJ não pode ser composto por uma sequência de dígitos repetidos.',
};

/**
 * Realiza a validação completa de um CNPJ, retornando um resultado rico com
 * motivo e mensagem descritiva em caso de invalidez.
 *
 * Suporta tanto o CNPJ numérico tradicional quanto o novo CNPJ Alfanumérico
 * previsto pela IN RFB nº 2.229/2024, com vigência a partir de julho de 2026.
 *
 * O valor de entrada pode estar com ou sem máscara, em qualquer capitalização.
 *
 * @param valor - CNPJ a validar. Aceita `null` e `undefined`.
 * @returns `ResultadoValidacaoCnpj` com `valido: true` e `cnpjNormalizado`
 *          em caso de sucesso, ou `valido: false` com `motivo` e `mensagem`
 *          em caso de falha.
 */
export function validarCnpj(valor: string | null | undefined): ResultadoValidacaoCnpj {
  if (valor == null) {
    const motivo = MotivoCnpjInvalido.Nulo;
    return { valido: false, motivo, mensagem: MENSAGENS[motivo] };
  }

  const normalizado = normalizarCnpj(valor);

  if (normalizado.length === 0) {
    const motivo = MotivoCnpjInvalido.Vazio;
    return { valido: false, motivo, mensagem: MENSAGENS[motivo] };
  }

  if (normalizado.length !== 14) {
    const motivo = MotivoCnpjInvalido.TamanhoInvalido;
    return { valido: false, motivo, mensagem: MENSAGENS[motivo] };
  }

  // Posições 0–11: aceita [0-9A-Z] (alfanumérico)
  for (let i = 0; i < 12; i++) {
    const code = normalizado.charCodeAt(i);
    const ehDigito = code >= COD_0 && code <= COD_9;
    const ehLetra = code >= COD_A && code <= COD_Z;
    if (!ehDigito && !ehLetra) {
      const motivo = MotivoCnpjInvalido.CaracteresInvalidos;
      return { valido: false, motivo, mensagem: MENSAGENS[motivo] };
    }
  }

  // Posições 12–13: apenas [0-9] (dígitos verificadores são sempre numéricos)
  for (let i = 12; i < 14; i++) {
    const code = normalizado.charCodeAt(i);
    if (code < COD_0 || code > COD_9) {
      const motivo = MotivoCnpjInvalido.DigitosVerificadoresNaoNumericos;
      return { valido: false, motivo, mensagem: MENSAGENS[motivo] };
    }
  }

  if (SEQUENCIAS_BLOQUEADAS.has(normalizado)) {
    const motivo = MotivoCnpjInvalido.SequenciaRepetida;
    return { valido: false, motivo, mensagem: MENSAGENS[motivo] };
  }

  // Validar DV1: calculado sobre os 12 primeiros caracteres
  const dv1Calculado = calcularDigitoVerificador(normalizado.substring(0, 12), PESOS_DV1);
  const dv1Informado = normalizado.charCodeAt(12) - 48;
  if (dv1Calculado !== dv1Informado) {
    const motivo = MotivoCnpjInvalido.DigitoVerificadorInvalido;
    return { valido: false, motivo, mensagem: MENSAGENS[motivo] };
  }

  // Validar DV2: calculado sobre os 13 primeiros caracteres (12 base + DV1)
  const dv2Calculado = calcularDigitoVerificador(normalizado.substring(0, 13), PESOS_DV2);
  const dv2Informado = normalizado.charCodeAt(13) - 48;
  if (dv2Calculado !== dv2Informado) {
    const motivo = MotivoCnpjInvalido.DigitoVerificadorInvalido;
    return { valido: false, motivo, mensagem: MENSAGENS[motivo] };
  }

  return { valido: true, cnpjNormalizado: normalizado };
}

/**
 * Verifica de forma simplificada se um CNPJ é válido.
 *
 * Equivalente a `validarCnpj(valor).valido`.
 *
 * @param valor - CNPJ a verificar. Aceita `null` e `undefined`.
 * @returns `true` se o CNPJ for válido, `false` caso contrário.
 */
export function ehCnpjValido(valor: string | null | undefined): boolean {
  return validarCnpj(valor).valido;
}
